import {
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
} from "@mui/material";
import {
  SettlementHeaderCells,
  UnitValueCell,
} from "components/utils/LiquidationDetails";
import { SETTLEMENT_TYPES } from "constants/index";
import { arsFormatter, roundToThree, formatDateOnlyDMY } from "helpers";
import {
  liquidationArticleOf,
  fceOf,
  liquidationLogValue,
  liquidationPalletStorageSubtotal,
  liquidationPalletsSubtotalTotal,
  liquidationPalletsUnitsTotal,
  liquidationStaySubtotalTotal,
  liquidationStayUnitsTotal,
  liquidatitonUnitValue,
  liquidationVolumenSubtotalTotal,
  liquidationVolumenUnitsTotal,
  calculateBillablePallets,
  liquidationContainerOf,
  liquidationArticleVolumeWeight,
} from "helpers/liquidations";
import React, { useMemo } from "react";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import {
  ArticleLocationLog,
  LiquidationAggregation,
  LiquidationSettlementType,
} from "types";

export default function Row({
  date,
  logsOfDay,
  liquidation,
  settlementType,
  print,
  hasDetail,
  isAbono = false,
  abonoUnitValue = 0,
  showMinimumAdjustmentColumn = false,
}: {
  date: string;
  logsOfDay: ArticleLocationLog[];
  liquidation: LiquidationAggregation;
  settlementType: LiquidationSettlementType;
  print: boolean;
  hasDetail: boolean;
  isAbono?: boolean;
  abonoUnitValue?: number;
  showMinimumAdjustmentColumn?: boolean;
}) {
  const [open, setOpen] = React.useState(print);

  const groupedLogsOfDay = useMemo(() => {
    const groups = new Map<string, ArticleLocationLog[]>();
    logsOfDay.forEach((log) => {
      const article = liquidationArticleOf(liquidation, log.article_id);
      const container = liquidationContainerOf(liquidation, article, log);
      const containerId = container && (
        "container_id" in container ? container.container_id : container.id
      );
      const key = JSON.stringify([
        log.article_id,
        log.container_id ?? containerId ?? article?.container_id,
        container?.description,
        container?.m3_min,
        container?.m3_max,
        container?.volume_weight,
        Number(log.physical_weight ?? article?.weight ?? 0),
        log.volume_weight ?? liquidationArticleVolumeWeight(article, container),
      ]);
      const group = groups.get(key);
      if (group) group.push(log);
      else groups.set(key, [log]);
    });
    return Array.from(groups.values());
  }, [liquidation, logsOfDay]);

  const unitsTotal = useMemo(() => {
    return liquidationStayUnitsTotal(settlementType, liquidation, logsOfDay);
  }, [liquidation, settlementType, logsOfDay]);

  const palletsUnitsTotal = useMemo(() => {
    return liquidationPalletsUnitsTotal(settlementType, liquidation, logsOfDay);
  }, [logsOfDay, liquidation, settlementType]);

  const palletsSubtotalTotal = useMemo(() => {
    return liquidationPalletsSubtotalTotal(
      settlementType,
      liquidation,
      logsOfDay,
    );
  }, [logsOfDay, liquidation, settlementType]);

  const volumenUnitsTotal = useMemo(() => {
    return liquidationVolumenUnitsTotal(settlementType, liquidation, logsOfDay);
  }, [logsOfDay, liquidation, settlementType]);
  //Se reemplazaron los cálculos separados de unidades/pallets/volumen por settlementUnitsTotal por motivos de comparaciones con los minimos. 20261106 - SHC-716.

  const settlementUnitsTotal = useMemo(() => {
    if (settlementType.settlement_type === SETTLEMENT_TYPES.PALLETS) {
      return liquidationPalletsUnitsTotal(settlementType, liquidation, logsOfDay);
    }

    if (settlementType.settlement_type === SETTLEMENT_TYPES.VOLUMEN) {
      return liquidationVolumenUnitsTotal(settlementType, liquidation, logsOfDay);
    }

    return liquidationStayUnitsTotal(settlementType, liquidation, logsOfDay);
  }, [settlementType, liquidation, logsOfDay]);

  const billableUnitsTotal = useMemo(() => {
    const minimumValue = Number(settlementType.minimum_value ?? 0);

    if (isAbono || minimumValue <= 0) return Number(settlementUnitsTotal ?? 0);

    return Math.max(Number(settlementUnitsTotal ?? 0), minimumValue);
  }, [isAbono, settlementType.minimum_value, settlementUnitsTotal]);

  const displayedSubtotal = useMemo(() => {
    if (isAbono) return abonoUnitValue;

    return billableUnitsTotal * Number(settlementType.calculation ?? 0);
  }, [
    isAbono,
    abonoUnitValue,
    billableUnitsTotal,
    settlementType.calculation,
  ]);

  return (
    <React.Fragment>
      <TableRow>
        <TableCell>
          {!print && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <BsArrowUp /> : <BsArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{formatDateOnlyDMY(date)}</TableCell>

        {settlementType.settlement_type !== SETTLEMENT_TYPES.ABONO && (
          <TableCell>{roundToThree(settlementUnitsTotal || 0)}</TableCell>
        )}

        {showMinimumAdjustmentColumn && (
          <TableCell>
            {billableUnitsTotal > Number(settlementUnitsTotal ?? 0)
              ? roundToThree(billableUnitsTotal)
              : "-"}
          </TableCell>
        )}

        <TableCell>{arsFormatter(displayedSubtotal)}</TableCell>
      </TableRow>

      {hasDetail && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Detalle del Día
                </Typography>
                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      {SettlementHeaderCells(settlementType.settlement_type)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedLogsOfDay.map((logs, index) => {
                      const log = {
                        ...logs[0],
                        quantity: logs.reduce((sum, stockLog) => sum + Number(stockLog.quantity ?? 0), 0),
                      };
                      const article = liquidationArticleOf(
                        liquidation,
                        log.article_id,
                      );
                      const unitsPerPallet = article?.units_per_pallet ?? 0;
                      const realPallets =
                        unitsPerPallet > 0 ? Number(log.quantity ?? 0) / unitsPerPallet : 0;
                      const billablePallets = logs.reduce(
                        (sum, stockLog) => sum + calculateBillablePallets(
                          Number(stockLog.quantity ?? 0),
                          Number(unitsPerPallet ?? 0),
                        ), 0,
                      );

                      const container = liquidationContainerOf(liquidation, article, log);
                      const articleWeight = Number(log.physical_weight ?? article?.weight ?? 0);
                      const articleVolumeWeight = log.volume_weight ?? liquidationArticleVolumeWeight(article, container);
                      const selectedWeightValue =
                        Math.max(articleWeight, articleVolumeWeight);
                      const selectedWeightTotal = Number(log.quantity ?? 0) * selectedWeightValue;
                      const isWeightSettlement =
                        settlementType.settlement_type === SETTLEMENT_TYPES.PESO ||
                        settlementType.settlement_type === SETTLEMENT_TYPES.PESO_VOLUMETRICO;

                      const fce = fceOf(liquidation, log.article_id);
                      const palletSubtotal = logs.reduce(
                        (sum, stockLog) => sum + liquidationPalletStorageSubtotal(
                          settlementType,
                          liquidation,
                          stockLog,
                        ), 0,
                      );

                      return (
                        <TableRow key={index}>
                          <TableCell>
                            {
                              liquidation.liquidation_articles.find(
                                (article) =>
                                  article.article_id === log.article_id,
                              )?.code
                            }
                          </TableCell>
                          <TableCell>
                            {
                              liquidation.liquidation_articles.find(
                                (article) =>
                                  article.article_id === log.article_id,
                              )?.description
                            }
                          </TableCell>
                          <TableCell>{log.quantity}</TableCell>

                          {isWeightSettlement && (
                            <>
                              <TableCell>{roundToThree(articleWeight)}</TableCell>
                              <TableCell>{roundToThree(articleVolumeWeight)}</TableCell>
                              <TableCell>{roundToThree(selectedWeightValue)}</TableCell>
                              <TableCell>{roundToThree(selectedWeightTotal)}</TableCell>
                            </>
                          )}

                          {settlementType.settlement_type !==
                            SETTLEMENT_TYPES.ABONO &&
                            settlementType.settlement_type !==
                              SETTLEMENT_TYPES.PALLETS &&
                            settlementType.settlement_type !==
                              SETTLEMENT_TYPES.VOLUMEN &&
                            settlementType.settlement_type !== 
                              SETTLEMENT_TYPES.UNIDADES &&
                              !isWeightSettlement &&
                            UnitValueCell(
                              settlementType.settlement_type,
                              liquidation,
                              undefined,
                              log,
                            )}

                          {settlementType.settlement_type !==
                            SETTLEMENT_TYPES.ABONO &&
                            settlementType.settlement_type !==
                              SETTLEMENT_TYPES.PALLETS &&
                            settlementType.settlement_type !==
                              SETTLEMENT_TYPES.VOLUMEN && 
                            settlementType.settlement_type !== 
                              SETTLEMENT_TYPES.UNIDADES &&(
                              <>
                                <TableCell>
                                  {roundToThree(
                                    liquidationLogValue(
                                      liquidation,
                                      settlementType,
                                      [log],
                                      log.article_id,
                                    ),
                                  )}
                                </TableCell>
                                <TableCell>
                                  {arsFormatter(settlementType.calculation)}
                                </TableCell>
                                <TableCell>
                                  {arsFormatter(
                                    liquidationLogValue(
                                      liquidation,
                                      settlementType,
                                      [log],
                                      log.article_id,
                                    ) * settlementType.calculation,
                                  )}
                                </TableCell>
                              </>
                            )}

                          {settlementType.settlement_type ===
                            SETTLEMENT_TYPES.PALLETS && (
                            <>
                              <TableCell>
                                {roundToThree(realPallets)}
                              </TableCell>
                              <TableCell>
                                {roundToThree(billablePallets)}
                              </TableCell>
                              <TableCell>
                                {arsFormatter(settlementType.calculation)}
                              </TableCell>
                              <TableCell>
                                {roundToThree(fce)}
                              </TableCell>
                              <TableCell>
                                {arsFormatter(palletSubtotal)}
                              </TableCell>
                            </>
                          )}

                          {settlementType.settlement_type ===
                            SETTLEMENT_TYPES.VOLUMEN && (
                            <>
                              <TableCell>
                                {liquidatitonUnitValue(
                                  settlementType.settlement_type,
                                  liquidation,
                                  undefined,
                                  log,
                                )}
                              </TableCell>

                              <TableCell>
                                {roundToThree(
                                  liquidationLogValue(
                                    liquidation,
                                    settlementType,
                                    [log],
                                    log.article_id,
                                  ),
                                )}
                              </TableCell>

                              <TableCell>
                                {arsFormatter(settlementType.calculation)}
                              </TableCell>

                              <TableCell>
                                {arsFormatter(
                                  liquidationLogValue(
                                    liquidation,
                                    settlementType,
                                    [log],
                                    log.article_id,
                                  ) * settlementType.calculation,
                                )}
                              </TableCell>
                            </>
                          )}

                          {settlementType.settlement_type === 
                            SETTLEMENT_TYPES.UNIDADES && !isWeightSettlement && (
                            <>
                              <TableCell>
                                {arsFormatter(settlementType.calculation)}
                              </TableCell>
                              <TableCell>
                                {arsFormatter(Number(log.quantity ?? 0) * settlementType.calculation)}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}
